import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/utils';
import { LEVEL_DOT_ACTIVE_CLASSES, LEVEL_DOT_IDLE_CLASSES, levelIndex } from './treeLevelStyles';

interface TreeViewLevelDotsProps {
  /** Počet úrovní stromu (= počet teček) */
  levelCount: number;
  /** Aktivní úroveň hromadného rozbalení (1-based) */
  baseLevel: number;
  onLevelClick: (level: number) => void;
}

/**
 * Tečky hromadného rozbalení (PHX-TREE-01 § 3): jedna 12px tečka na úroveň
 * stromu, spojené linkami 14×1 px, v barvách úrovní. Aktivní tečka je
 * vyplněná barvou své úrovně; klik = tvrdý reset rozbalení na úroveň.
 */
export function TreeViewLevelDots({ levelCount, baseLevel, onLevelClick }: TreeViewLevelDotsProps) {
  const { t } = useTranslation('sharedUi');

  return (
    <div
      className="pb-phx-4 flex items-center px-0.5"
      role="group"
      aria-label={t('treeView.expandLevels')}
    >
      {Array.from({ length: levelCount }, (_, i) => i + 1).map((level) => {
        const index = levelIndex(level - 1);
        const isActive = baseLevel === level;
        return (
          <Fragment key={level}>
            {level > 1 && <span className="bg-phx-border-2 h-px w-3.5 shrink-0" aria-hidden />}
            <button
              type="button"
              title={t('treeView.expandToLevel', { level })}
              aria-label={t('treeView.expandToLevel', { level })}
              aria-pressed={isActive}
              className={cn(
                'bg-phx-surface h-3 w-3 shrink-0 cursor-pointer rounded-full border-[1.5px] transition-colors',
                isActive ? LEVEL_DOT_ACTIVE_CLASSES[index] : LEVEL_DOT_IDLE_CLASSES[index]
              )}
              onClick={() => onLevelClick(level)}
            />
          </Fragment>
        );
      })}
    </div>
  );
}
